import { Controller } from "@hotwired/stimulus";

/**
 * Controller for directory-page filter dropdowns that navigate to /search.
 *
 * Used by all directory pages (/locations, /churches, /denominations/:slug,
 * /church_tags/:slug-churches). When a filter checkbox is selected, this
 * controller collects all selected filters plus the page's context
 * (lat/lng, location name, denomination, tag) and navigates to /search
 * with everything encoded in the URL.
 */
export default class extends Controller {
  static values = {
    lat: Number,
    lng: Number,
    location: String,
    denomination: String,
    tagCategory: String,
    tagName: String,
  };

  clearCheckboxes() {
    this.element
      .querySelectorAll('input[type="checkbox"]:checked')
      .forEach((input) => {
        input.checked = false;
      });
  }

  change() {
    const filters = this.collectFilters();
    const url = this.buildSearchUrl(filters);
    // Clear checkboxes before navigating so Turbo caches clean state
    this.clearCheckboxes();
    window.location.href = url;
  }

  collectFilters() {
    const filters = {};

    // Include denomination if set (for denomination-specific pages)
    if (this.denominationValue) {
      filters.denomination = [this.denominationValue];
    }

    // Include the current page's tag if set (for tag-specific pages).
    // tagCategoryValue is already a /search filter key ("programs", "music", etc.)
    if (this.tagCategoryValue && this.tagNameValue) {
      filters[this.tagCategoryValue] = [this.tagNameValue];
    }

    this.element
      .querySelectorAll('input[type="checkbox"]:checked')
      .forEach((input) => {
        // Parse name like "f[programs][]" to get the filter key
        const match = input.name.match(/f\[(\w+)\]\[\]/);
        if (match) {
          const key = match[1];
          if (!filters[key]) {
            filters[key] = [];
          }
          filters[key].push(input.value);
        }
      });

    return filters;
  }

  buildSearchUrl(filters) {
    const params = new URLSearchParams();

    if (this.latValue && this.lngValue) {
      params.set("lat", this.latValue);
      params.set("lng", this.lngValue);
      if (this.locationValue) {
        params.set("location", this.locationValue);
      }
    }

    // Build filter params in Rails hash format: f[key][]=value
    Object.entries(filters).forEach(([key, values]) => {
      values.forEach((value) => {
        params.append(`f[${key}][]`, value);
      });
    });

    return `/search?${params.toString()}`;
  }
}
